import * as React from 'react';
import { Link } from 'react-router-dom';

import { getWebEnvironment } from '@/env';

import { isOrganizerLink, resolveOrganizerLink } from './constants';
import { safeHref } from './rich-text-panel';

/**
 * **The one component that renders a URL an editor typed.**
 *
 * ## Why it is its own file now
 *
 * It began inside `sections-panel.tsx`, because a hero's Button link was the only content URL the
 * site rendered. Then `CMS-014` added a footer, a navigation bar and a row of social links, and
 * `CMS-015` added a case study card — four more places an editor types an address, in three more
 * files. `javascript:` in a footer link is exactly as live as `javascript:` in a call to action,
 * and the first time that hole opened it was because the check lived next to *one* of the places
 * that needed it.
 *
 * So the check moved to the thing every one of them renders. `every-editor-supplied-url-is-checked
 * .test.ts` scans the whole marketing surface for an `href` or a `to` that did not come through
 * here, which is the shape of the mistake rather than an instance of it.
 *
 * ## What it does
 *
 * The address goes through `safeHref` — an allow-list of `http`, `https`, `mailto` and
 * site-relative paths, refusing `javascript:` by not listing it rather than by naming it.
 *
 * A refused address renders as **plain text**: the words survive, the link does not. Dropping the
 * label as well would silently remove something somebody put on the page on purpose, leaving no
 * sign that anything was wrong.
 *
 * An address that stays inside the site routes through the router rather than reloading the whole
 * application, because an editor types a whole URL and `https://h2hspark.com/pricing` is a page
 * the router already has.
 *
 * **Forwards its props and its ref**, which is not optional: `Button asChild` renders its child
 * *instead of* a button and passes the styling down to it. A component that accepts `children` and
 * ignores everything else swallows those props silently, and the call to action renders as bare
 * text that still works and looks like nothing. That is exactly what happened on the first pass,
 * and it is invisible to a type checker.
 */
export const ContentLink = React.forwardRef<
  HTMLAnchorElement,
  { readonly href: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>
>(function ContentLink({ href, children, ...rest }, ref) {
  // ── `organizer:/start` and `organizer:/login`, before anything else ────────────────────────
  //
  // The organizer console is a different application on a different origin, and which origin is
  // a build-time value — so content cannot hold its address and has to name the destination
  // instead (`constants.ts`, `ORGANIZER_LINK_PREFIX`). Resolved here, then put through `safeHref`
  // like every other address: the resolved value is an `http(s)` URL built from this build's own
  // configuration, and it earns no exemption from the allow-list for having been computed.
  //
  // An `organizer:` address this build will not honour falls through as plain text rather than
  // reaching `safeHref`, which would refuse the unknown scheme and reach the same place — said
  // explicitly so the reason is the path and not the colon.
  const resolved = isOrganizerLink(href)
    ? resolveOrganizerLink(href, getWebEnvironment().organizerUrl)
    : href;

  const safe = resolved === null ? null : safeHref(resolved);

  if (safe === null) return <span ref={ref as React.Ref<HTMLSpanElement> & never}>{children}</span>;

  if (safe.startsWith('/') || safe.startsWith('#')) {
    return (
      <Link to={safe} ref={ref} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={safe} rel="noreferrer" ref={ref} {...rest}>
      {children}
    </a>
  );
});
